import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { apiService } from '../../services/apiService';
import { BudgetStatus } from '../../types';
import { BudgetApiService } from '../../services/budgetApiService';

interface BudgetExportModalProps {
  open: boolean;
  onClose: () => void;
}

export const BudgetExportModal: React.FC<BudgetExportModalProps> = ({ open, onClose }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setError(null);

    if (!startDate || !endDate) {
      setError('Debe seleccionar ambas fechas');
      return;
    }

    if (startDate > endDate) {
      setError('La fecha inicio no puede ser mayor que la fecha fin');
      return;
    }

    setLoading(true);
    try {
      // Llamar al servicio para descargar el Excel
      const response = await apiService.budgets.exportExcel(startDate, endDate, status || undefined);
      
      // Crear blob y descargar
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extraer nombre de archivo del header Content-Disposition si está disponible
      const filename = `presupuestos_${startDate}_a_${endDate}.xlsx`;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Cerrar modal después de descargar
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Error al descargar el archivo');
      console.error('Error exportando Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStartDate('');
    setEndDate('');
    setStatus('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Exportar Presupuestos a Excel</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Fecha Inicio"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Fecha Fin"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label="Estado"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value={BudgetStatus.DRAFT}>{BudgetApiService.getStatusLabel(BudgetStatus.DRAFT)}</MenuItem>
              <MenuItem value={BudgetStatus.PENDING_APPROVAL}>{BudgetApiService.getStatusLabel(BudgetStatus.PENDING_APPROVAL)}</MenuItem>
              <MenuItem value={BudgetStatus.APPROVED}>{BudgetApiService.getStatusLabel(BudgetStatus.APPROVED)}</MenuItem>
              <MenuItem value={BudgetStatus.REJECTED}>{BudgetApiService.getStatusLabel(BudgetStatus.REJECTED)}</MenuItem>
              <MenuItem value={BudgetStatus.SENT}>{BudgetApiService.getStatusLabel(BudgetStatus.SENT)}</MenuItem>
              <MenuItem value={BudgetStatus.SOLD}>{BudgetApiService.getStatusLabel(BudgetStatus.SOLD)}</MenuItem>
              <MenuItem value={BudgetStatus.LOST}>{BudgetApiService.getStatusLabel(BudgetStatus.LOST)}</MenuItem>
            </Select>
          </FormControl>
          <Alert severity="info">
            Se descargarán los presupuestos cuya última versión cae en el rango de fechas
            seleccionado (y coincide con el estado, si elegiste uno) - uno por presupuesto,
            con las columnas: Proyecto, Cliente, Vendedor, Versión, Fecha, Estado, Total USD, Total ARS
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={loading || !startDate || !endDate}
          startIcon={loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
        >
          {loading ? 'Descargando...' : 'Descargar Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
